import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import {
  deleteUserInputSchema,
  IDeleteUserInput,
  USER_DELETION_GRACE_PERIOD_DAYS,
} from "muminsmart-utils";
import { useDeleteAccountMutation } from "@/lib/api/apiSlice";
import { useAuth } from "@/contexts/AuthContext";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { routes } from "@/lib/constants";
import { inter } from "@/lib/fonts";
import { useConfirm } from "@/hooks/use-confirm";
import ConfirmDialog from "@/app/components/common/ConfirmAlert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import ButtonsHeader from "../common/ButtonsHeader";

export default function DeleteAccount() {
  const { logout } = useAuth();
  const [deleteAccount] = useDeleteAccountMutation();
  const [showPassword, setShowPassword] = useState(false);
  const { isOpen, confirm, handleCancel, handleConfirm, title, description } =
    useConfirm();

  const form = useForm<IDeleteUserInput>({
    resolver: zodResolver(deleteUserInputSchema),
    defaultValues: {
      password: "",
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (values: IDeleteUserInput) => {
    const confirmed = await confirm({
      title: "Delete your account?",
      description: `Your account will be deactivated immediately. You can log back in within ${USER_DELETION_GRACE_PERIOD_DAYS} days to restore it. After that, your account and personal data will be permanently removed.`,
    });

    if (!confirmed) {
      return;
    }

    try {
      const response = await deleteAccount({
        password: values.password,
      }).unwrap();

      toast.success(response.message);
      await logout(routes.auth.login);
    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to delete account"));
    }
  };

  return (
    <>
      <ButtonsHeader heading="Delete Account" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={`px-5 space-y-5 ${inter.className}`}
        >
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
            <p className="font-medium">
              This action will schedule your account for deletion.
            </p>
            <p className="mt-2 text-destructive/90">
              Your addresses, cart, wishlist, and wallet data will be removed if
              you do not log back in within {USER_DELETION_GRACE_PERIOD_DAYS}{" "}
              days. Your orders and reviews will be kept without your personal
              account link.
            </p>
          </div>

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">
                  Enter your password to confirm
                </FormLabel>
                <FormControl>
                  <InputGroup className="h-auto rounded-full bg-white">
                    <InputGroupInput
                      type={showPassword ? "text" : "password"}
                      placeholder="********"
                      className="px-5 py-5"
                      autoComplete="current-password"
                      {...field}
                      aria-invalid={
                        form.formState.errors.password ? "true" : "false"
                      }
                    />
                    <InputGroupAddon align="inline-end">
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className="text-muted"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff size={30} />
                        ) : (
                          <Eye size={30} />
                        )}
                      </button>
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={isSubmitting}
            variant="destructive"
            className="w-full rounded-full"
          >
            {isSubmitting ? "Deleting account..." : "Delete my account"}
          </Button>
        </form>
      </Form>

      <ConfirmDialog
        isOpen={isOpen}
        title={title}
        description={description}
        confirmButtonLabel="Delete account"
        cancelButtonLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
